import type { ReactNode } from "react";

/**
 * The full-bleed brand gradient.
 *
 * ⚠ The unauthenticated screens are the ONE exception to the rule that the gradient is an
 * accent and never sits behind content (`unifiber-design`, and the design note in
 * CLAUDE.md). Nothing inside the application may use this. If a signed-in screen wants a
 * gradient, it wants `.gradient-band` on a header strip instead.
 */
export function GradientScene({ children }: { children: ReactNode }) {
  return (
    <div className="bg-scene relative flex min-h-screen font-sans antialiased">
      <div className="absolute left-8 top-8 z-10 lg:left-10">
        <img src="/assets/unifiber-logo-white.svg" alt="Unifiber" className="h-10 opacity-90 lg:h-12" />
      </div>

      {/* Hidden below lg: at phone width the card is the whole screen and the wordmark
          moves inside it, exactly as in mockups/01-login.html. */}
      <div className="relative z-10 hidden flex-col items-start justify-center pl-20 pr-12 lg:flex lg:w-1/2">
        <h1 className="text-7xl font-bold tracking-tight text-white drop-shadow-lg">Cocon-Stats</h1>
        <p className="mt-4 max-w-md text-lg font-light text-white/70">
          Network reporting for Unifiber and its build partners.
        </p>
      </div>

      <div className="relative z-10 flex flex-1 items-center justify-center px-6 py-24 lg:py-12">
        <div className="glass-card-login w-full max-w-md space-y-6 rounded-2xl p-8 sm:p-10">
          <div className="text-center lg:hidden">
            <h2 className="text-3xl font-bold text-white">Cocon-Stats</h2>
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

/** The red banner used for every failure on the gradient screens. */
export function SceneError({ message, details }: { message: string; details?: string[] }) {
  if (!message) return null;
  return (
    <div
      role="alert"
      className="rounded-lg border border-red-400/30 bg-red-500/20 px-4 py-2.5 text-sm font-medium text-red-100"
    >
      {message}
      {details && details.length > 0 && (
        <ul className="mt-1.5 list-disc space-y-0.5 pl-4 text-xs font-normal text-red-100/90">
          {details.map((d) => (
            <li key={d}>{d}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The step eyebrow: "Step 2 of 4 — Authenticator". Matches the validated onboarding chain. */
export function SceneStep({ label }: { label: string }) {
  return <p className="text-[10px] font-bold uppercase tracking-wider text-white/45">{label}</p>;
}
